import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, MailCheck, Clock, KeyRound, ScanBarcode, Boxes, Users, ClipboardList, BarChart3, Tags, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT = 'info@thewellnessstore.co.nz';

const visitorId = () => {
  let id = localStorage.getItem('st_visitor');
  if (!id) { id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem('st_visitor', id); }
  return id;
};

const FEATURES = [
  { icon: ScanBarcode, title: 'A till that just works', text: 'Scan, sell, EFTPOS, refunds, gift cards, receipts. Sales never get blocked by a stock count.' },
  { icon: Boxes, title: 'Stock you can trust', text: 'Live stock across stores, short-dated markdowns, negative-stock fixes and a Smart PO that writes supplier orders for you.' },
  { icon: ClipboardList, title: 'Customer orders & requests', text: 'Special orders, "ordered & waiting", reminders and loyalty — nothing lives on a sticky note any more.' },
  { icon: Users, title: 'Built for a real team', text: 'Staff PINs, rosters, daily tasks, notice board and store chat — the morning routine runs itself.' },
  { icon: BarChart3, title: 'Numbers without the accountant', text: 'Daily targets, supplier and brand sales, till counts and a ready-to-file NZ GST return.' },
  { icon: Tags, title: 'Barcodes & labels', text: 'Print shelf labels and barcodes, link unknown scans in one tap, keep every product scannable.' },
];

export default function TryLanding() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [form, setForm] = useState({ name: '', store_name: '', email: '', website: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = 'Try SupplyTrack free for 1 hour';
    axios.post(`${API}/public/visit`, {
      visitor_id: visitorId(), path: '/try', src: params.get('src'), utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'), utm_campaign: params.get('utm_campaign'), referrer: document.referrer || null,
    }).catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/trial-request`, {
        ...form, src: params.get('src'), utm_source: params.get('utm_source'), referrer: document.referrer || null, visitor_id: visitorId(),
      });
      setDone({ email: form.email.trim().toLowerCase(), resent: !!r.data?.resent });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong — please try again or email us.');
    } finally { setSending(false); }
  };

  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="min-h-screen bg-[#F6F1E7] text-[#14281A]" data-testid="try-landing">
      {/* Top bar */}
      <header className="flex items-center justify-between px-5 sm:px-10 py-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#0B2E13] flex items-center justify-center text-[#E8B04B] font-heading font-bold text-lg">S</div>
          <span className="font-heading text-xl font-semibold tracking-wide uppercase">SupplyTrack</span>
        </div>
        <Link to="/login" className="text-sm font-medium text-[#0B2E13] underline underline-offset-4 decoration-[#E8B04B]" data-testid="try-signin-link">Already a customer? Sign in</Link>
      </header>

      {/* Hero */}
      <section className="bg-[#0B2E13] text-[#F6F1E7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#F6F1E7 0.8px, transparent 0.8px)', backgroundSize: '22px 22px' }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-10 py-14 lg:py-20 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-start">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            <p className="inline-block text-[11px] uppercase tracking-[0.25em] text-[#E8B04B] border border-[#E8B04B]/40 rounded-full px-3 py-1 mb-6">Built by a retailer · Made in New Zealand</p>
            <h1 className="font-heading font-bold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl" data-testid="try-headline">
              A POS built by a retailer<br />who was <span className="text-[#E8B04B]">fed up.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg max-w-xl text-[#F6F1E7]/85 leading-relaxed">
              After spending thousands on expensive POS systems that still couldn't do the most basic day-to-day jobs in my stores,
              I built one for my own business. Six months of real trading across two shops later, it's ready for yours.
            </p>
            <ul className="mt-8 space-y-2.5 text-sm text-[#F6F1E7]/90">
              {['Ring up a sale in under ten seconds, even when stock says zero', 'Supplier orders written for you from what actually sells', 'Staff, rosters, tasks and chat in the same place as the till', 'Your NZ GST return filled in from your own sales and invoices'].map(t => (
                <li key={t} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-[#E8B04B] shrink-0" />{t}</li>
              ))}
            </ul>
            <p className="mt-10 text-sm text-[#F6F1E7]/60">— Ritu, owner of The Wellness Store (Orewa & Eastridge)</p>
          </div>

          {/* Trial form */}
          <div className="bg-[#F6F1E7] text-[#14281A] rounded-2xl p-6 sm:p-8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150" data-testid="try-form-card">
            {done ? (
              <div data-testid="try-success">
                <div className="w-12 h-12 rounded-full bg-[#0B2E13] text-[#E8B04B] flex items-center justify-center mb-4"><MailCheck className="w-6 h-6" /></div>
                <h2 className="font-heading text-3xl font-bold uppercase leading-none">Check your inbox</h2>
                <p className="mt-3 text-sm text-[#14281A]/80">
                  {done.resent ? 'A fresh temporary password' : 'Your temporary password'} is on its way to <b>{done.email}</b>. If it isn't there in a couple of minutes, check your spam folder.
                </p>
                <ol className="mt-5 space-y-3 text-sm">
                  <li className="flex gap-3"><span className="font-heading text-lg font-bold text-[#E8B04B] w-5">1</span>Open the email and click the sign-in link.</li>
                  <li className="flex gap-3"><span className="font-heading text-lg font-bold text-[#E8B04B] w-5">2</span>Your hour starts the moment you first sign in — so sign in when you've got the time to play.</li>
                  <li className="flex gap-3"><span className="font-heading text-lg font-bold text-[#E8B04B] w-5">3</span><span>Need more time? Email <a className="underline decoration-[#E8B04B]" href={`mailto:${CONTACT}`}>{CONTACT}</a> and we'll extend it.</span></li>
                </ol>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="try-form">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0B2E13]/60">Free · no card · no call</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase leading-none mt-1">Try it for 1 hour</h2>
                <p className="mt-2 text-sm text-[#14281A]/70">A full working store with a real catalogue, sample stock and sales. Your hour starts when you first sign in.</p>
                <div className="mt-6 space-y-3">
                  <Input placeholder="Your name" required minLength={2} {...field('name')} className="h-12 bg-white border-[#0B2E13]/15" data-testid="try-name" />
                  <Input placeholder="Your store's name" required minLength={2} {...field('store_name')} className="h-12 bg-white border-[#0B2E13]/15" data-testid="try-store" />
                  <Input type="email" placeholder="Email for your login" required {...field('email')} className="h-12 bg-white border-[#0B2E13]/15" data-testid="try-email" />
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
                </div>
                {error && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="try-error">{error}</p>}
                <Button type="submit" disabled={sending} className="mt-5 w-full h-12 rounded-full bg-[#0B2E13] hover:bg-[#14451E] text-[#F6F1E7] text-base font-semibold" data-testid="try-submit">
                  {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up your store…</> : <>Send my trial login <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
                <p className="mt-3 text-[11px] text-[#14281A]/60 leading-relaxed">We'll email you a temporary password — nothing else. No newsletter, no sales calls.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 py-14 grid md:grid-cols-3 gap-8">
        {[{ icon: MailCheck, t: 'Enter your email', d: 'We set up a demo store in your name — your own login, your store\'s name on the receipts.' },
          { icon: KeyRound, t: 'Sign in with the temporary password', d: 'Straight into a live-looking shop: catalogue, stock, sales history, staff room and reports.' },
          { icon: Clock, t: 'You get one full hour', d: 'The clock starts at your first sign-in. Want longer or a walk-through? Email us and we extend it.' }].map((s, i) => (
          <div key={s.t} className="flex gap-4" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="w-11 h-11 rounded-xl bg-[#0B2E13] text-[#E8B04B] flex items-center justify-center shrink-0"><s.icon className="w-5 h-5" /></div>
            <div><h3 className="font-heading text-xl font-semibold uppercase leading-tight">{s.t}</h3><p className="mt-1 text-sm text-[#14281A]/70">{s.d}</p></div>
          </div>
        ))}
      </section>

      {/* Features */}
      <section className="bg-white/60 border-y border-[#0B2E13]/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase leading-none max-w-xl">The basic jobs, done properly — <span className="text-[#2E7D32]">every single day</span></h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9">
            {FEATURES.map(f => (
              <div key={f.title} className="group">
                <f.icon className="w-6 h-6 text-[#2E7D32] transition-transform group-hover:-translate-y-0.5" />
                <h3 className="mt-3 font-semibold text-base">{f.title}</h3>
                <p className="mt-1 text-sm text-[#14281A]/70 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing + footer */}
      <section className="max-w-6xl mx-auto px-5 sm:px-10 py-14 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase leading-none">Simple pricing, no lock-in</h2>
          <p className="mt-4 text-sm md:text-base text-[#14281A]/75 max-w-xl">
            <b>$300 + GST per store, per month</b> and a one-off <b>$1,000 + GST</b> onboarding where we load your catalogue, set up your stores and staff and walk your team through it.
            Monthly invoice, paid by bank transfer. Leave whenever you like.
          </p>
        </div>
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center justify-center h-12 px-7 rounded-full bg-[#E8B04B] text-[#0B2E13] font-semibold hover:bg-[#f0bd5f] transition-colors" data-testid="try-cta-bottom">
          Start my free hour <ArrowRight className="w-4 h-4 ml-2" />
        </a>
      </section>
      <footer className="px-5 sm:px-10 py-6 text-xs text-[#14281A]/60 flex flex-wrap items-center justify-between gap-2 border-t border-[#0B2E13]/10">
        <span>SupplyTrack · made in Orewa, New Zealand by The Wellness Store</span>
        <a href={`mailto:${CONTACT}`} className="underline decoration-[#E8B04B]">{CONTACT}</a>
      </footer>
    </div>
  );
}
